/**
 * Profession contract templates.
 *
 * A guide's "Use this template" button links to /signup?template=<id>. The id
 * is remembered in localStorage (see TemplateCapture in App.tsx), survives the
 * signup/login flow, and CreateContractForm seeds a new contract from it the
 * first time it opens — so the reader lands in a contract already written for
 * their kind of work instead of an empty form.
 *
 * Text in [brackets] is meant to be replaced by the freelancer.
 */

export type ContractTemplate = {
  id: string;
  /** Shown on the guide button: "Use the web developer template". */
  label: string;
  /** Must be one of CreateContractForm's PROJECT_TYPES. */
  projectType: string;
  projectDesc: string;
  coreDeliverable: string;
  outOfScope: string;
  includedRevisions: number;
  intellectualProperty: 'Client owns all upon payment' | 'Shared ownership' | 'Freelancer retains all rights';
  /** Profession-specific clauses added under the agreement's additional terms. */
  customTerms: string;
};

export const CONTRACT_TEMPLATES: Record<string, ContractTemplate> = {
  'web-developer': {
    id: 'web-developer',
    label: 'web developer',
    projectType: 'Web Development',
    projectDesc:
      'Design and development of a [number]-page responsive website for [client business], built with [technology], deployed to the client’s hosting.',
    coreDeliverable:
      '- [Number] responsive pages: [list pages]\n- Source code delivered through a [GitHub/GitLab] repository\n- Deployment to the client’s hosting account\n- [Number] days of post-launch bug fixes for defects in the agreed scope',
    outOfScope:
      '- Content writing, copy and images\n- Hosting, domain and third-party licence fees\n- New features or pages beyond the list above\n- Ongoing maintenance after the bug-fix period\n- SEO and marketing services',
    includedRevisions: 2,
    intellectualProperty: 'Client owns all upon payment',
    customTerms:
      '1. Credentials and access: The Client will provide hosting, domain and third-party account access needed for the work. The Service Provider will hand over all project credentials on final payment.\n2. Third-party code: Open-source libraries keep their own licences. The Service Provider will list material open-source components on delivery.\n3. Bug fixes: Defects in the agreed scope reported within the bug-fix period are fixed free of charge. New features are quoted as change requests.',
  },
  'graphic-designer': {
    id: 'graphic-designer',
    label: 'graphic designer',
    projectType: 'Graphic Design',
    projectDesc:
      'Design of [deliverables, e.g. a logo and brand identity] for [client business], delivered as final artwork in the formats listed below.',
    coreDeliverable:
      '- [Number] initial concepts\n- Final artwork for the selected concept\n- Final files in [PNG, SVG, PDF] at print and web sizes\n- [Editable source files (AI/PSD/Figma), if included]',
    outOfScope:
      '- Printing and production costs\n- Stock images or fonts that need a paid licence\n- Additional concepts after the initial round\n- Copywriting\n- Design work for other campaigns or materials',
    includedRevisions: 2,
    intellectualProperty: 'Client owns all upon payment',
    customTerms:
      '1. Source files: Editable source files are [included / available for an additional fee of ₹[amount]].\n2. Fonts and stock assets: The Client is responsible for buying licences for any paid fonts or stock images used in the final design.\n3. Portfolio: The Service Provider may show the final work in its portfolio after public launch, unless the Client asks otherwise in writing.',
  },
  'video-editor': {
    id: 'video-editor',
    label: 'video editor',
    projectType: 'Video Editing',
    projectDesc:
      'Editing of [number] videos of up to [length] minutes each for [platform, e.g. YouTube], from footage supplied by the client.',
    coreDeliverable:
      '- [Number] edited videos up to [length] minutes each\n- Colour correction, audio clean-up and background music from licensed libraries\n- Captions/subtitles in [language]\n- Final export in [resolution and format] for [platform]',
    outOfScope:
      '- Shooting, scripting or voice-over\n- Paid music, stock footage or plugin licences beyond the agreed library\n- Thumbnails and channel graphics\n- Re-edits after final approval\n- Raw project files unless agreed below',
    includedRevisions: 2,
    intellectualProperty: 'Client owns all upon payment',
    customTerms:
      '1. Footage: The Client confirms it owns or has the rights to all footage, music and material it supplies.\n2. Project files: Editing project files are [not included / included for an additional fee of ₹[amount]].\n3. Turnaround: Each edit is delivered within [number] working days of receiving complete footage; delays in footage extend the deadline by the same period.',
  },
  'content-writer': {
    id: 'content-writer',
    label: 'content writer',
    projectType: 'Content Writing',
    projectDesc:
      'Writing of [number] articles of about [word count] words each on topics agreed with [client business].',
    coreDeliverable:
      '- [Number] original articles of about [word count] words each\n- Topics and keywords as agreed in writing before each article\n- Delivered as Google Docs with headings and meta descriptions\n- Plagiarism-free, original work',
    outOfScope:
      '- Publishing or formatting in the client’s CMS\n- Images and graphics\n- Keyword research beyond the agreed list\n- Rewrites after final approval\n- Social media posts',
    includedRevisions: 2,
    intellectualProperty: 'Client owns all upon payment',
    customTerms:
      '1. Byline: The Client may publish the work under its own name or brand. [The Service Provider may / may not be credited.]\n2. Originality: The Service Provider confirms each article is original and not copied from another source.\n3. Briefs: The Client provides a brief or topic for each article; writing starts once the brief is confirmed.',
  },
};

export const PENDING_TEMPLATE_KEY = 'defellix_pending_contract_template';

export function rememberTemplate(id: string): boolean {
  if (!CONTRACT_TEMPLATES[id]) return false;
  try {
    window.localStorage.setItem(PENDING_TEMPLATE_KEY, id);
    return true;
  } catch {
    return false;
  }
}

/** The template waiting to be applied, without consuming it. */
export function peekPendingTemplate(): ContractTemplate | undefined {
  try {
    const id = window.localStorage.getItem(PENDING_TEMPLATE_KEY);
    return id ? CONTRACT_TEMPLATES[id] : undefined;
  } catch {
    return undefined;
  }
}

/** Returns the pending template and clears it, so it is applied only once. */
export function takePendingTemplate(): ContractTemplate | undefined {
  const t = peekPendingTemplate();
  try {
    window.localStorage.removeItem(PENDING_TEMPLATE_KEY);
  } catch {
    // ignore
  }
  return t;
}
